import { Link } from "react-router-dom";
import { ShieldCheck, FileCheck2, Building2, Check, ArrowRight, Phone, AlertTriangle } from "lucide-react";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/site";

const LANDLORD_IMG =
  "https://images.unsplash.com/photo-1676210133055-eab6ef033ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwbHVtYmVyJTIwaGVhdGluZyUyMGVuZ2luZWVyfGVufDB8fHx8MTc4MTg2NTUyMXww&ixlib=rb-4.1.0&q=85";

export default function LandlordPage() {
  return (
    <>
      <Seo
        title="Landlord CP12 Gas Safety Certificates | Hackney & Islington | EL-Gas"
        description="Same-day CP12 landlord gas safety certificates in Hackney, Islington and Lower Clapton. Portfolio pricing for agents. Gas Safe No. 604161."
        path="/landlord-certificates"
      />
      <section className="bg-brand-navy text-white py-20" data-testid="landlord-hero">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="overline text-brand-orange">CP12 Landlord Gas Safety Certificates</span>
            <h1 className="mt-3 font-heading font-black text-5xl md:text-6xl tracking-tighter">
              Official Gas Safety Inspections for{" "}
              <span className="text-brand-orange">London Landlords &amp; Agents</span>
            </h1>
            <p className="mt-5 text-slate-300 text-lg max-w-2xl">
              UK law requires every privately rented property with gas appliances to hold a valid
              annual CP12 certificate. We issue same-day, digitally signed certificates across
              Hackney, Islington, Lower Clapton and the wider E5 area.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/booking" className="btn-primary" data-testid="landlord-book-cta">
                Book a CP12 inspection <ArrowRight className="w-5 h-5" />
              </Link>
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-outline-white" data-testid="landlord-call-cta">
                <Phone className="w-5 h-5" /> {COMPANY.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative">
              <div className="absolute -top-4 -right-4 left-4 bottom-4 border-2 border-brand-orange rounded-sm" />
              <img
                src={LANDLORD_IMG}
                alt="Engineer inspecting gas appliance"
                className="relative w-full h-[440px] object-cover rounded-sm shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Legal explainer */}
      <section className="py-24 bg-white" data-testid="landlord-legal-section">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <span className="overline">The Law (Plain English)</span>
            <h2 className="mt-3 font-heading font-black text-4xl tracking-tighter text-brand-navy">
              What every landlord in Hackney &amp; Islington needs to know.
            </h2>
            <div className="mt-6 prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                Under the <strong>Gas Safety (Installation and Use) Regulations 1998</strong>, any
                landlord renting out a property with gas appliances, pipework or flues must arrange an
                annual safety check by a Gas Safe registered engineer. The resulting record —
                commonly called the <strong>CP12</strong> — must be issued within 28 days and given to
                tenants within 28 days of the inspection.
              </p>
              <p>
                Tenants of new lets must be supplied with a copy <em>before</em> they move in. Records
                must be retained by the landlord for at least <strong>two years</strong>. Failing to comply
                can result in unlimited fines and, in severe cases, prosecution.
              </p>
              <p>
                We carry out the full inspection, complete the paperwork on the day, and email a
                digital PDF copy to you and your tenants directly.
              </p>
            </div>

            <ul className="mt-10 grid sm:grid-cols-2 gap-4">
              {[
                "Every gas appliance tested & logged",
                "Flue & ventilation checks included",
                "Tenant-ready PDF emailed same day",
                "Reminder a month before next renewal",
                "Discounted bundles for 3+ properties",
                "Trusted by Hackney & Islington estate agents",
              ].map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-sm"
                >
                  <Check className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" />
                  <span className="text-sm font-semibold text-slate-800">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-5 space-y-6">
            {/* Bulk booking callout */}
            <div className="bg-slate-50 p-8 rounded-sm border-t-4 border-brand-orange shadow-sm" data-testid="bulk-booking-callout">
              <Building2 className="w-9 h-9 text-brand-orange" />
              <h3 className="mt-4 font-heading font-black text-2xl tracking-tight text-brand-navy">
                Managing multiple properties?
              </h3>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Contact us directly for corporate estate agent and landlord portfolio pricing
                packages. We work with letting agencies across Hackney &amp; Islington and offer fixed-rate quarterly
                renewal bundles.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {[
                  "Volume pricing from 3+ properties",
                  "Single-point-of-contact account manager",
                  "Synced renewal calendar",
                  "Consolidated monthly invoicing",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-slate-700">
                    <Check className="w-4 h-4 text-brand-orange mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-primary w-full mt-6" data-testid="bulk-booking-cta">
                Request portfolio quote
              </Link>
            </div>

            <div className="bg-brand-navy text-white p-7 rounded-sm">
              <FileCheck2 className="w-8 h-8 text-brand-orange" />
              <p className="mt-3 font-heading font-bold text-xl">
                Same-day digital CP12 issued.
              </p>
              <p className="mt-2 text-sm text-slate-300">
                PDF lands in your inbox before our engineer leaves the property.
              </p>
            </div>

            <div className="p-5 border border-amber-300 bg-amber-50 rounded-sm flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-900">
                <strong>Renewal coming up?</strong> Book at least 14 days before expiry to avoid
                non-compliance gaps.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-slate-50" data-testid="landlord-process-section">
        <div className="container-x">
          <span className="overline">Our 4-step process</span>
          <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tighter text-brand-navy max-w-3xl">
            From booking to certificate in under 24 hours.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Book online", b: "Pick a slot in 60 seconds — morning or afternoon." },
              { n: "02", t: "Engineer dispatched", b: "Local van, Gas Safe ID-carded, fully insured." },
              { n: "03", t: "Full inspection", b: "Every appliance tested, flue & ventilation checked." },
              { n: "04", t: "PDF delivered", b: "Digital CP12 emailed to you and tenants the same day." },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 p-7 rounded-sm shadow-sm hover:shadow-md transition-shadow">
                <p className="font-heading font-black text-5xl text-brand-orange tracking-tighter">
                  {s.n}
                </p>
                <h4 className="mt-3 font-heading font-bold text-xl text-brand-navy">{s.t}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-orange text-white py-14">
        <div className="container-x flex flex-col md:flex-row items-center justify-between gap-6">
          <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            Ready to get your CP12 issued?
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/booking" className="inline-flex items-center gap-2 bg-white text-brand-navy px-7 py-4 font-bold rounded-sm hover:bg-slate-100 transition-colors">
              Book inspection <ArrowRight className="w-5 h-5" />
            </Link>
            <a href={`tel:${COMPANY.phoneTel}`} className="btn-secondary">
              <Phone className="w-5 h-5" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
